import { TwoUIBiCustomClick, useTwoUITelemetry, useTwoUITelemetryIds } from "@amx/ux-twoui-telemetry";
import { useTheme } from "@amx/ux-twoui-theming";
import {
    Announced,
    ColumnActionsMode,
    ContextualMenu,
    DetailsColumn,
    DetailsColumnBase,
    DetailsList as FluentDetailsList,
    DetailsListLayoutMode,
    DirectionalHint,
    IColumn,
    IContextualMenuItem,
    IContextualMenuProps,
    IDetailsColumnProps,
    IDetailsHeaderProps,
    IRenderFunction,
    SelectionMode,
    Stack as FluentStack,
    Sticky,
    StickyPositionType
} from "@fluentui/react";
import React, { useState } from "react";
import { useComponentLogger } from "../../../shared/logging";
import { BaseProps } from "../../../shared/props";
import { validateComponent } from "../../../shared/validation";
import { Text } from "../../Text/Text";
import { TruncatedText } from "../../TruncatedText/TruncatedText";
import { CustomColumn } from "../ListItemColumns/CustomColumn/CustomColumn";
import { ListItem, ListItemProps } from "../ListItems/ListItem/ListItem";
import { MobileListItem, MobileListItemProps } from "../ListItems/MobileListItem/MobileListItem";
import { ListWrapper } from "../ListWrapper/ListWrapper";
import { ListItemStyleProps, ListWrapperCollapseProps } from "../shared/props";
import { childrenAsElementArray, onRenderCell } from "../shared/utils";
import {
    isInteractiveListItemColumn,
    matchingRowsAndHeadersDetailsListValidator,
    minDetailsListRowsValidator,
    sortStatusDetailsListValidator
} from "./DetailsList.helpers";
import {
    columnHeaderDefaultMinWidth,
    contextualMenuGapSpace,
    detailsListItemHeaderColumnStyle,
    getContextualMenuStyles,
    getDetailsColumnStyles,
    getDetailsListStyles,
    getScrollablePaneStackStyles,
    getStackWrapperStyles,
    headerCellStyle,
    headerListItemPadding,
    listItemHeaderStackStyles,
} from "./DetailsList.styles";
import { DetailsListHeaderColumn } from "./DetailsList.types";

export interface DetailsListInternalProps extends BaseProps, ListItemStyleProps {
    /**
     * Column definitions for column headers.
     * If none are provided, default columns will be created based on the items' properties.
     */
    readonly headerColumns: readonly DetailsListHeaderColumn[];
    /**
     * Accessible label for the list header.
     */
    readonly ariaLabelForListHeader: string;
    /**
     * Accessible label for the row check button, e.g. "select row".
     */
    readonly checkButtonAriaLabel: string;
    /**
     * A list of items where each item has to be one of our defined list item
     * types - note that all of these items, as they extend ListItemBaseProps,
     * can have more ListItems as children so the nested structure arises from
     * the tree-like structure of the list items
     */
    readonly children?: React.ReactNode;
    /**
     * Optional callback invoked when the header columns are changing.
     */
    readonly onHeaderChange?: (headerColumns: readonly DetailsListHeaderColumn[], ev?: React.MouseEvent<HTMLElement>) => void;
    /**
     * Optional announcement to describe how the list is initially sorted.
     * This value can be updated in the passed in functions for column header sorting.
     */
    readonly announcedSortMessage?: string;
    /**
     * Optional custom height for the DetailsList. If this is passed in, the DetailsList
     * will render with a scrollbar and a sticky header if the list items do not fit within the height.
     */
    readonly customHeight?: number | string;

}

export type DetailsListProps = DetailsListInternalProps & ListWrapperCollapseProps;

export const DetailsList: React.FC<DetailsListProps> = (props: DetailsListProps) => {
    useComponentLogger("DetailsList", props.id);

    return (
        <ListWrapper {...props} listType="DetailsList" />
    );
};

export const DetailsListInternal: React.FC<DetailsListInternalProps> = ({
    headerColumns,
    ariaLabelForListHeader,
    checkButtonAriaLabel,
    children,
    announcedSortMessage,
    onHeaderChange,
    customHeight,
    ...otherProps
}) => {

    const radixBaseValue = 10;

    const theme = useTheme();
    const twoUITelemetry = useTwoUITelemetry();
    const pageArea = useTwoUITelemetryIds().areaBiId;
    const numMobileListItems = 2; // Number of items in a mobile list item
    let childrenAsArray = childrenAsElementArray(children).slice();

    validateComponent(
        "DetailsList",
        [
            minDetailsListRowsValidator(childrenAsArray),
            matchingRowsAndHeadersDetailsListValidator(childrenAsArray[0].props.columns?.length ?? numMobileListItems, headerColumns),
            sortStatusDetailsListValidator(headerColumns)
        ]
    );

    // Sort initially sorted columns
    let initialColSorted = false;
    const length = headerColumns.length;
    let colIndex = 0;

    while (!initialColSorted && colIndex < length) {
        const col = headerColumns[colIndex];
        switch (col.sortStatus) {
            case "sortedAscending":
            case "sortedDescending":
                const [sortedItems, _] = col.sortFunction(childrenAsArray, colIndex, col.sortStatus === "sortedDescending");
                childrenAsArray = [...sortedItems];
                initialColSorted = true;
        }
        colIndex++;
    }
    colIndex++;

    // These are menu props that contain sort options for a column header's drop down menu
    const [contextualMenuProps, setContextualMenuProps] = useState<IContextualMenuProps>();
    const [sortMessage, setSortMessage] = useState(announcedSortMessage);

    /**
     * Function to sort a column in the DetailsList using the column header's passed in sorting function
     */
    const onSortColumn = (column: IColumn, isSortedDescending: boolean, biData: string): void => {
        // create the custom click event
        const clickEvent: TwoUIBiCustomClick = {
            clickId: `${biData}`,
            pageArea: pageArea
        };
        // send the event
        twoUITelemetry.reportClick(clickEvent);

        const currDetailsHeaderArray = column.data.detailsHeaderArray as DetailsListHeaderColumn[];
        const columnIndex = currDetailsHeaderArray.findIndex(header => header.key === column.key);

        if (columnIndex >= 0 && column.data) {
            const currHeader = column.data.detailsHeaderColumn as DetailsListHeaderColumn;
            switch (currHeader.sortStatus) {
                case "notSorted":
                case "sortedDescending":
                case "sortedAscending":
                    const [sortedItems, message] = currHeader.sortFunction(column.data.detailsListItems, columnIndex, isSortedDescending);
                    if (sortedItems) {

                        setSortMessage(`${currHeader.name} ${message}`);

                        // Update the detailsHeaderColumns
                        const newDetailsHeaderArray = currDetailsHeaderArray.map(
                            (c, index) => index === columnIndex ? { ...c, sortStatus: isSortedDescending ? "sortedDescending" : "sortedAscending" } :
                                { ...c, sortStatus: c.sortStatus === "notSortable" ? c.sortStatus : "notSorted" });

                        if (onHeaderChange) {
                            onHeaderChange(newDetailsHeaderArray as DetailsListHeaderColumn[]);
                        }
                    }
            }
        }
    };

    const onContextualMenuDismissed = (): void => {
        setContextualMenuProps(undefined);
    };

    /**
     * Get the ContextualMenuProps of the current column header
     */
    function getContextualMenuProps(ev: React.MouseEvent<HTMLElement>, column: IColumn): IContextualMenuProps {

        const currHeader = column.data.detailsHeaderColumn as DetailsListHeaderColumn;
        const menuItems: IContextualMenuItem[] = [];

        // Map SortOptions[] to IContextualMenuItem[]
        switch (currHeader.sortStatus) {
            case "notSorted":
            case "sortedDescending":
            case "sortedAscending":
                currHeader.sortOptionsMenu.forEach((sortOption) => {
                    menuItems.push(
                        {
                            ...sortOption,
                            checked: sortOption.checked(column.isSorted ?? false, column.isSortedDescending ?? false),
                            role: "menuitemradio",
                            onClick: () => onSortColumn(column, sortOption.isSortedDescending, sortOption["data-bi-id"])
                        });
                });
                break;
            default:
                break;
        }

        return {
            items: menuItems ?? [],
            target: ev.currentTarget as HTMLElement,
            directionalHint: DirectionalHint.bottomLeftEdge,
            gapSpace: contextualMenuGapSpace,
            onDismiss: onContextualMenuDismissed,
            styles: getContextualMenuStyles(theme)
        };
    }

    /**
     * Callback for when the context menu of an item has been accessed
     */
    const onItemContextMenu = (_item?: any, index?: number, ev?: Event): boolean => {
        const menuProps: IContextualMenuProps = {
            target: ev?.target as HTMLElement,
            items: [
                {
                    key: "text",
                    name: `selected`,
                },
            ],
            onDismiss: () => {
                setContextualMenuProps(undefined);
            },
        };

        if (index && index > -1) {
            setContextualMenuProps(menuProps);
        }

        return false;
    };

    const onColumnHeaderClick = (ev: React.MouseEvent<HTMLElement>, column: IColumn): void => {
        // create the custom click event
        const clickEvent: TwoUIBiCustomClick = {
            clickId: `${column.data.detailsHeaderColumn["data-bi-id"]}`,
            pageArea: pageArea
        };
        // send the event
        twoUITelemetry.reportClick(clickEvent);

        if (column.columnActionsMode !== ColumnActionsMode.disabled) {
            setContextualMenuProps(getContextualMenuProps(ev, column));
        }
    };

    /**
     * Callback for when the user opens the column header context menu
     */
    const onColumnContextMenu = (column?: IColumn, ev?: React.MouseEvent<HTMLElement>): void => {
        if (column && ev && column.columnActionsMode !== ColumnActionsMode.disabled) {
            setContextualMenuProps(getContextualMenuProps(ev, column));
        }
    };

    /**
     * Custom rendering for column header content, long text can either truncate or wrap
     * (Note: This does not control the rendering of the sort button)
     */
    const onRenderColumnHeader: IRenderFunction<IDetailsColumnProps> = (colProps) => {
        const currHeader = colProps?.column.data.detailsHeaderColumn;
        // Check for whether the header is of type listItemHeader (content is ListItemColumn) or the default type (content is regular text)
        switch (currHeader?.headerType) {
            case "listItemHeader":
                /**
                 * If the header is interactive (i.e. a button) and sortable, it must be rendered outside of the ColumnHeader
                 * to avoid nested interactable elements and abide by accessibility standards
                 */
                const isInteractiveListItemHeader = isInteractiveListItemColumn(currHeader.listItemColumn) && currHeader.sortStatus !== "notSortable";
                return isInteractiveListItemHeader ? <></> : currHeader.listItemColumn;
            default:
                // By default, the ColumnHeader content renders as text that either truncates or wraps (icon prefix is optional)
                const headerTruncates = !colProps?.column.isMultiline ?? false;
                return (
                    headerTruncates ?
                        (
                            <TruncatedText
                                type="text"
                                lines={1}
                                variant={"headerText"}
                                stopPropagation={false}
                            >
                                {colProps?.column.name}
                            </TruncatedText>
                        ) :
                        <Text children={colProps?.column.name} variant={"headerText"} nowrap={false} block={true} />
                );
        }
    };

    /**
     * Function to map TwoUI DetailsList headers to FluentUI IColumns
     */
    const mapHeaderColumns = (detailsHeaderColumns: readonly DetailsListHeaderColumn[], listItemsArray: readonly JSX.Element[]) => {
        return detailsHeaderColumns.map((column) => {
            // Check if column is sorted
            const columnIsSorted = column.sortStatus === "sortedAscending" || column.sortStatus === "sortedDescending";
            // Create base props for the header column
            const baseProps: IColumn = {
                key: column.key,
                minWidth: column.minWidth ? parseInt(column.minWidth, radixBaseValue) : parseInt(columnHeaderDefaultMinWidth, radixBaseValue),
                name: column.name,
                ariaLabel: column.ariaLabel,
                onRenderHeader: onRenderColumnHeader,
                columnActionsMode: column.sortStatus === "notSortable" ? ColumnActionsMode.disabled : !columnIsSorted ? ColumnActionsMode.hasDropdown : undefined,
                isSorted: columnIsSorted,
                isSortedDescending: column.sortStatus === "sortedDescending",
                onColumnClick: onColumnHeaderClick,
                onColumnContextMenu: onColumnContextMenu,
                data: {
                    detailsHeaderColumn: column,
                    detailsListItems: listItemsArray,
                    detailsHeaderArray: detailsHeaderColumns
                },
                isResizable: true
            };

            // Switch case for the type of ColumnHeader: listItemHeader or default type
            switch (column.headerType) {
                case "listItemHeader":
                    return baseProps;
                default:
                    const textTruncates = column.textTruncates ?? false;
                    return {
                        ...baseProps,
                        iconName: column.iconName,
                        isIconOnly: column.isIconOnly, // whether to display only an Icon in the header
                        isMultiline: !textTruncates,
                        onRenderHeader: column.isIconOnly ? undefined : onRenderColumnHeader // use default FluentUI onRenderHeader function if there is no text
                    };
            }
        });

    };

    const fluentOnRenderCell = (listItem?: JSX.Element) =>
        onRenderCell(listItem!);

    /**
     * This function determines how each individual HeaderColumn will render (it wraps header content).
     * FluentUI's DetailsColumn component provides sort functionality.
     * (Note: To change how the header content is rendered, reference the onRenderColumnHeader function)
     *
     * This function handles the rendering of four cases of ColumnHeaders:
     * 1) ColumnHeader of type listItemHeader that is interactive and not sortable
     * 2) ColumnHeader of type listItemHeader that is interactive and sortable
     * 3) ColumnHeader of type listItemHeader that is non-interactive
     * 4) ColumnHeader that is not of type listItemHeader
     */
    const getDetailsColumn = (header: DetailsListHeaderColumn, col: IColumn, index: number) => {
        // Switch case for whether the ColumnHeader content is of type listItemHeader or regular text
        switch (header.headerType) {
            /* tslint:disable no-switch-case-fall-through */
            // tslint:disable-next-line
            // @ts-ignore
            case "listItemHeader":
                // Case for if ColumnHeader content is of type listItemHeader and is interactive
                if (isInteractiveListItemColumn(header.listItemColumn)) {
                    if (header.sortStatus === "notSortable") {
                        // If the ColumnHeader of type listItemHeader is not sortable, rendering of content will be handled by the "onRenderColumnHeader" function
                        return <DetailsColumnBase column={col} columnIndex={index} styles={detailsListItemHeaderColumnStyle} />;
                    } else {
                        /**
                         * If the ColumnHeader of type listItemHeader is sortable, in order to avoid nested interactive elements,
                         * render the listItemColumn and sorting icon within a stack.
                         * The DetailsColumn will handle the sort functionality.
                         */
                        return (
                            <CustomColumn>
                                <FluentStack horizontal={true} styles={listItemHeaderStackStyles} >
                                    {header.listItemColumn}
                                    <DetailsColumn column={col} columnIndex={index} cellStyleProps={headerCellStyle} styles={getDetailsColumnStyles(index, false, true)} />
                                </FluentStack>
                            </CustomColumn>
                        );
                    }
                    // Falls through to default case if listItemColumn is non-interactive
                }
            /**
             * If the ColumnHeader is of type listItemHeader and is non-interactive or if ColumnHeader is regular text,
             * rendering of content will be handled by the "onRenderColumnHeader" function
             */
            default:
                return <DetailsColumn column={col} columnIndex={index} cellStyleProps={headerCellStyle} styles={getDetailsColumnStyles(index, col.isIconOnly ?? false)} />;
        }
    };

    /**
     * An override passed to the FluentUI DetailsList to render the details header.
     * This function determines how the entire details header row will render.
     * (Note: To change how each HeaderColumn is rendered, use the getDetailsColumn function)
     */
    const onRenderDetailsHeader = (props?: IDetailsHeaderProps) => {
        // Check if children is MobileListItem, otherwise default to using ListItem
        if (props?.columns[0].data) {
            // Props for mobile details header (2 columns)
            const detailsListItems = props?.columns[0].data.detailsListItems;
            if (detailsListItems[0].props.leftColumn) {
                const mobileListItemProps: MobileListItemProps = {
                    "aria-label": props?.ariaLabel,
                    leftColumn: props ? getDetailsColumn(props?.columns[0].data.detailsHeaderColumn, props?.columns[0], 0) : <></>,
                    rightColumn: props ? getDetailsColumn(props?.columns[1].data.detailsHeaderColumn, props?.columns[1], 1) : <></>,
                    onClickProps: undefined,
                    listItemRole: "row",
                    customListItemPadding: headerListItemPadding
                };

                // Render details header as a TwoUI MobileListItem
                return (
                    customHeight ?
                        (
                            // The details header is sticky meaning it stays in place when the user scrolls vertically
                            <Sticky stickyPosition={StickyPositionType.Header}>
                                <MobileListItem {...mobileListItemProps} />
                            </Sticky>
                        ) :
                        <MobileListItem {...mobileListItemProps} />
                );
            } else {
                // Propos for regular details header
                const listItemProps: ListItemProps = {
                    "aria-label": props?.ariaLabel,
                    variant: detailsListItems[0].props.variant,
                    columns: props ? props.columns.map((col, index) => {
                        return getDetailsColumn(props?.columns[index].data.detailsHeaderColumn, col, index);
                    }) : [],
                    onClickProps: undefined,
                    listItemRole: "row",
                    horizontalStack: false,
                    verticalAlign: "stretch",
                    customListItemPadding: headerListItemPadding
                };

                // Render details header as a TwoUI ListItem
                return (
                    customHeight ?
                        (
                            // The details header is sticky meaning it stays in place when the user scrolls vertically
                            <Sticky stickyPosition={StickyPositionType.Header}>
                                <ListItem {...listItemProps} />
                            </Sticky>
                        ) :
                        <ListItem {...listItemProps} />
                );
            }
        }

        return <></>;
    };

    const mappedHeaderColumns = mapHeaderColumns(headerColumns, childrenAsArray);

    // Mapping to FluentUI's DetailsList
    const detailsListElements = [
        sortMessage ? <Announced message={sortMessage} /> : undefined,
        (
            <FluentDetailsList
                items={childrenAsArray}
                columns={mappedHeaderColumns}
                onItemContextMenu={onItemContextMenu}
                ariaLabelForListHeader={ariaLabelForListHeader}
                checkButtonAriaLabel={checkButtonAriaLabel}
                listProps={{
                    items: childrenAsArray,
                    onRenderCell: fluentOnRenderCell,
                    ...otherProps
                }
                }
                layoutMode={DetailsListLayoutMode.fixedColumns}
                onRenderDetailsHeader={onRenderDetailsHeader}
                styles={getDetailsListStyles(theme)}
                selectionMode={SelectionMode.none}
            />
        ),
        contextualMenuProps && <ContextualMenu {...contextualMenuProps} />
    ];

    return (
        <FluentStack styles={getStackWrapperStyles()}>{
            customHeight ?
                (
                    // Vertical scrollbar for when the user passes in custom height
                    <FluentStack styles={getScrollablePaneStackStyles(customHeight)}>
                        {detailsListElements}
                    </FluentStack>
                ) :
                (
                    <>
                        {detailsListElements}
                    </>
                )
        }
        </FluentStack>
    );
};
